import { compose } from '@doltech/utils/lib/compose';
import { isNotEmpty } from '@doltech/utils/lib/text';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import { getSnapshot, onSnapshot } from 'mobx-state-tree';
import { useCreateStore, useProvider } from 'mobx-store-provider';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { RenderElementProps } from 'slate-react';
import styled from 'styled-components';

import { findBellowByType } from '../../../../../DolEditor/helpers/nodeValue';
import {
  useBlankV2DoTestStore,
  withBlankV2DoTestStore,
} from '../../../../plugins-common/BlankV2/store/do-test.store';
import { BLANK_V2 } from '../../../../plugins-common/BlankV2/types';
import { CORRECT_ANSWER, CORRECT_ANSWERS } from '../../../../plugins-common/CorrectAnswers/types';
import {
  useExplanationsStore,
  withExplanationsStore,
} from '../../../../plugins-common/Explanations/store/explanations.store';
import { useDoExerciseProgressStore } from '../../../../shared-components/Exercise/do-exercise-progress-store.store';
import { ExerciseInCompleteNotice } from '../../../../shared-components/Exercise/ExerciseInCompleteNotice';
import { useExerciseMultipleChoiceAdminContext } from '../../../ExerciseMultipleChoice/components/AdminContext';
import { ExerciseGapFillDoTestStore } from '../../store/exercise-gap-fill-do-test.store';
import { ExerciseGapFillOptions } from '../../types';
import { withRootElement } from '../../../../../../slatePlugins/common/hocs/current-element.store';
import { flatten } from 'lodash';
import { withPortalId } from '../../../../plugins-common/Explanations/store/withPortalId';

const Main = styled.div`
  white-space: normal;

  &.have-guided {
    margin-top: 12px !important;
  }
`;

/* eslint-disable no-nested-ternary */
type DoTestExerciseGapFillProps = RenderElementProps &
  Pick<ExerciseGapFillOptions, 'onBackWhenInComplete'>;

export const DoTestExerciseGapFillBase = (props: DoTestExerciseGapFillProps) => {
  const { attributes, children, element, onBackWhenInComplete } = props;
  const adminContext = useExerciseMultipleChoiceAdminContext();
  const blankStore = useBlankV2DoTestStore();
  const explanStore = useExplanationsStore();
  const pageExerciseStore = useDoExerciseProgressStore();
  const externalStore = pageExerciseStore?.gapFillStores.get(element.id);
  const Provider = useProvider(ExerciseGapFillDoTestStore);
  const internalStore = useCreateStore(ExerciseGapFillDoTestStore, {
    id: element?.id || nanoid(),
  });
  const isInLanding = adminContext === null;
  const haveGuided = element?.children?.[0]?.present === 'gap-fill-guide';

  const produceQuestionInfo = React.useCallback(() => {
    const allCorrectAnswers = flatten(
      findBellowByType(element, CORRECT_ANSWERS).map((ans) => ans.children)
    );

    const correctAnswers = {};

    for (const correctAnswer of allCorrectAnswers) {
      const answer = correctAnswer?.correctAnswer?.toLowerCase()?.trim();
      if (correctAnswer.forId)
        correctAnswers[correctAnswer.forId] = {
          id: correctAnswer.forId,
          correctAnswers: answer.includes('/')
            ? answer.split('/').map((ans) => ans.trim())
            : [answer],
        };
    }

    return correctAnswers;
  }, [element]);

  /** Set data from internalStore -> externalStore */
  React.useEffect(() => {
    if (!externalStore && internalStore && pageExerciseStore) {
      pageExerciseStore.init([internalStore.preparePayload() as any]);
    }
  }, [externalStore, pageExerciseStore, internalStore]);

  /** Push question info to interal store */
  React.useEffect(() => {
    internalStore.setQuestionInfos(produceQuestionInfo());
  }, [produceQuestionInfo, internalStore]);

  /** Has data from outside, here, we don't need internal store anymore.
   * if the external store already has isCompleted=true (or isViewResult=true)
   * -> set appropriate data then show the reuslt
   *  */
  React.useEffect(() => {
    if (externalStore) {
      externalStore.setQuestionInfos(produceQuestionInfo());
      blankStore.setUserAnswers(getSnapshot(externalStore.userAnswers));
      if (externalStore.isViewResult) {
        externalStore.check();
        blankStore.viewResult();
        explanStore.viewResult();
      }
    }
  }, [produceQuestionInfo, externalStore, blankStore, explanStore]);

  /** Pull from blank store, auto save progress */
  React.useEffect(() => {
    return onSnapshot(blankStore, (snapshot) => {
      if (externalStore) {
        externalStore.setUserAnswers(snapshot.userAnswers);
      }
      internalStore.setUserAnswers(snapshot.userAnswers);
    });
  }, [blankStore, externalStore, internalStore]);

  /**
   * All correctAnswers are filled and at least 1 1 blank
   */
  const isValid = React.useMemo(() => {
    const blanks = findBellowByType(element, BLANK_V2);
    const correctAnswers = findBellowByType(element, CORRECT_ANSWER);
    const allCorrectAnswersFilled = correctAnswers.every((correctAnswer) =>
      isNotEmpty(correctAnswer.correctAnswer)
    );
    return blanks.length && allCorrectAnswersFilled;
  }, [element]);

  return (
    <Provider value={externalStore ?? internalStore}>
      <Main
        className={cl('do-test-exercise-gap-fill', 'font-inter', {
          'is-valid': isValid,
          'have-guided': haveGuided,
          'is-in-landing': isInLanding,
        })}
        {...attributes}
      >
        {isValid ? (
          children
        ) : adminContext ? (
          <ExerciseInCompleteNotice
            text="Gap Fill"
            onBack={onBackWhenInComplete}
            missingFields={['Answer']}
          />
        ) : null}
      </Main>
    </Provider>
  );
};

export const DoTestExerciseGapFill = compose(
  withPortalId,
  withExplanationsStore,
  withBlankV2DoTestStore,
  withRootElement,
  observer
)(DoTestExerciseGapFillBase);
